import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import api from "../infra/api";

const getUsersUrl = () =>
  `${process.env.REACT_APP_API_URL || ""}api/settings/users`;

async function fetchSystemUsers(): Promise<SystemUser[]> {
  try {
    const response = await api(getUsersUrl());
    if (response.status >= 300) return [];
    const data = await response.json();
    const list = data?.users ?? (Array.isArray(data) ? data : []);
    return list;
  } catch {
    return [];
  }
}

export interface ModalForwardAttendanceProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
  currentUserId?: number | string | null;
  onSuccess?: () => void | Promise<void>;
}

interface SystemUser {
  id?: number | string;
  codigo?: string;
  nome?: string;
  nomecomp?: string;
  email?: string;
  img_usuario?: string;
}

const ModalForwardAttendance: React.FC<ModalForwardAttendanceProps> = ({
  open,
  onClose,
  sessionId,
  currentUserId,
  onSuccess,
}) => {
  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | string | null>(
    null,
  );
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedUserId(null);
      setSearchQuery("");
      fetchSystemUsers().then(setSystemUsers);
    }
  }, [open]);

  const filteredUsers = useMemo(() => {
    const excludeCurrent = systemUsers.filter(
      (u) => u.id !== currentUserId && u.codigo !== currentUserId,
    );
    if (!searchQuery.trim()) return excludeCurrent;
    const q = searchQuery.trim().toLowerCase();
    return excludeCurrent.filter((u) => {
      const name = (u.nomecomp || u.nome || "").toLowerCase();
      const code = (u.codigo ?? u.id ?? "").toString().toLowerCase();
      const email = (u.email || "").toLowerCase();
      return name.includes(q) || code.includes(q) || email.includes(q);
    });
  }, [systemUsers, currentUserId, searchQuery]);

  const handleConfirm = async () => {
    if (selectedUserId == null) return;
    setSending(true);
    try {
      const url =
        process.env.REACT_APP_API_URL +
        `api/chat/sessions/${sessionId}/forward`;
      const response = await api(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_user_id: selectedUserId }),
      });
      const data = response.ok ? await response.json().catch(() => ({})) : null;
      if (response.ok && data?.success !== false) {
        await onSuccess?.();
        onClose();
        setSelectedUserId(null);
      } else {
        alert(data?.message || "Erro ao encaminhar atendimento.");
      }
    } catch {
      alert("Erro ao encaminhar atendimento.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SendRoundedIcon color="primary" />
        Encaminhar Atendimento
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, pt: 2, pb: 1 }}
        >
          Selecione o usuário do sistema para vincular este atendimento. O
          contato externo receberá uma mensagem informando a transferência.
        </Typography>
        <Box sx={{ px: 2, pb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar por nome, código ou e-mail..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon
                    sx={{ fontSize: 20, color: "text.secondary" }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense sx={{ maxHeight: 320, overflow: "auto", py: 0 }}>
          {filteredUsers.map((u) => {
            const uid = u.id ?? u.codigo;
            const name = u.nomecomp || u.nome || String(uid);
            const code = u.codigo ?? uid;
            const selected = selectedUserId === uid;
            const secondary = [code && `Código: ${code}`, u.email]
              .filter(Boolean)
              .join(" • ");
            return (
              <ListItem key={String(uid)} disablePadding>
                <ListItemButton
                  selected={selected}
                  onClick={() => setSelectedUserId(uid)}
                >
                  <ListItemAvatar>
                    <Avatar src={u.img_usuario} sx={{ width: 40, height: 40 }}>
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={secondary || undefined}
                  />
                  {selected && (
                    <Chip
                      size="small"
                      label="Selecionado"
                      color="primary"
                      sx={{ ml: 1 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        {systemUsers.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 3, textAlign: "center" }}
          >
            Carregando usuários...
          </Typography>
        )}
        {systemUsers.length > 0 && filteredUsers.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 3, textAlign: "center" }}
          >
            Nenhum usuário encontrado para &quot;{searchQuery}&quot;
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button size="small" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedUserId == null || sending}
          startIcon={
            sending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SendRoundedIcon fontSize="small" />
            )
          }
        >
          {sending ? "Encaminhando..." : "Encaminhar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalForwardAttendance;
